/**
 * gammaAttributionStore.ts — MER Algo Footprints v7.1B — Phase 6.5
 *
 * In-memory ring buffer for Gamma attribution records with JSONL export.
 *
 * ── Design ────────────────────────────────────────────────────────────────────
 * Holds the last MAX_RECORDS attribution records in memory.
 * Older records are evicted FIFO when the buffer is full.
 * No server/UI dependency — plain module-level state.
 *
 * Export produces newline-delimited JSON (JSONL) — one record per line.
 * JSONL is the smallest safe export format: streamable, appendable,
 * readable by any JSON tool, compatible with log rotation.
 *
 * Placement: src/domains/market/engines/gamma/gammaAttributionStore.ts
 */

import type { GammaAttributionRecord } from './gammaAttribution';
import { summarizeAttribution, GammaAttributionSummary } from './gammaAttributionSummary';
import { buildMergedBundle, GammaMergedAttributionBundle } from './gammaAttribution';

const MAX_RECORDS = 500;

const _buffer: GammaAttributionRecord[] = [];

// ─── Store API ────────────────────────────────────────────────────────────────

/**
 * Appends an attribution record to the in-memory buffer.
 * Evicts the oldest record when the buffer exceeds MAX_RECORDS.
 */
export function appendAttribution(record: GammaAttributionRecord): void {
  _buffer.push(record);
  if (_buffer.length > MAX_RECORDS) {
    _buffer.shift();
  }
}

/**
 * Returns a read-only snapshot of the current buffer contents.
 * Consumers receive copies — they cannot mutate the buffer.
 */
export function getAllAttribution(): GammaAttributionRecord[] {
  return _buffer.slice();
}

/**
 * Returns the N most recent attribution records (default 50).
 */
export function getRecentAttribution(n: number = 50): GammaAttributionRecord[] {
  return _buffer.slice(-n);
}

/**
 * Clears the buffer (useful for testing).
 */
export function clearAttribution(): void {
  _buffer.length = 0;
}

/**
 * Returns the current buffer length (without copying).
 */
export function attributionCount(): number {
  return _buffer.length;
}

// ─── Export API ───────────────────────────────────────────────────────────────

/**
 * Serializes all attribution records to JSONL (newline-delimited JSON).
 * One complete JSON object per line. Safe to append to a log file.
 * Returns an empty string when the buffer is empty.
 */
export function exportAttributionJSONL(): string {
  return _buffer.map(r => JSON.stringify(r)).join('\n');
}

/**
 * Serializes records to a minimal CSV string for spreadsheet import.
 * Includes only the most analytically useful columns.
 * Returns header + rows; empty string when buffer is empty.
 */
export function exportAttributionCSV(): string {
  if (!_buffer.length) return '';

  const COLS = [
    'id', 'capturedAt', 'symbol', 'setupType', 'setupDir',
    'regime', 'bias', 'dataQuality', 'gammaStale',
    'pinScore', 'expansionScore', 'directionalScore',
    'baseConfidence', 'confidenceAdjustment', 'finalConfidence', 'confidenceRule',
    'baseRankScore', 'rankingAdjustment', 'finalRankScore', 'rankingRule',
    'outcomeHorizon', 'outcomeReturn', 'outcomeMFE', 'outcomeMAE', 'outcomeSuccess',
  ] as const;

  const header = COLS.join(',');
  const rows   = _buffer.map(r =>
    COLS.map(col => {
      const v = (r as unknown as Record<string, unknown>)[col];
      if (v === null || v === undefined) return '';
      if (typeof v === 'string' && v.includes(',')) return `"${v}"`;
      return String(v);
    }).join(',')
  );

  return [header, ...rows].join('\n');
}

/**
 * Produces a GammaAttributionSummary from the current buffer.
 */
export function getSummary(): GammaAttributionSummary {
  return summarizeAttribution(_buffer.slice());
}

// ─── Correlation / merge helpers (Phase 6.5.1) ────────────────────────────────

/**
 * Returns all attribution records matching a given correlationKey.
 * Useful for finding the confidence and ranking records for the same decision.
 */
export function getAttributionByCorrelationKey(
  key: string,
): GammaAttributionRecord[] {
  return _buffer.filter(r => r.correlationKey === key);
}

/**
 * Builds merged attribution bundles by joining confidence and ranking records
 * that share the same evaluationCycleId (the explicit per-symbol-cycle identity).
 *
 * For each unique evaluationCycleId in the buffer, finds the first confidence-side
 * record (baseRankScore = null) and the first ranking-side record (baseRankScore != null),
 * then joins them via buildMergedBundle.
 *
 * Using evaluationCycleId as the join key (not correlationKey) ensures the join
 * is driven by explicit cycle identity, not timestamp proximity.
 *
 * Returns one bundle per unique evaluationCycleId.
 */
export function getMergedBundles(): GammaMergedAttributionBundle[] {
  const byId = new Map<string, { conf: GammaAttributionRecord | null; rank: GammaAttributionRecord | null }>();

  for (const rec of _buffer) {
    const id = rec.evaluationCycleId;
    if (!byId.has(id)) byId.set(id, { conf: null, rank: null });
    const entry = byId.get(id)!;
    const isConfRecord = rec.baseRankScore === null && rec.confidenceRule !== 'NOT_RUN';
    const isRankRecord = rec.baseRankScore !== null;
    if (isConfRecord && !entry.conf) entry.conf = rec;
    else if (isRankRecord && !entry.rank) entry.rank = rec;
  }

  const bundles: GammaMergedAttributionBundle[] = [];
  for (const { conf, rank } of byId.values()) {
    const bundle = buildMergedBundle(conf, rank);
    if (bundle) bundles.push(bundle);
  }
  return bundles;
}
